<template>
  <div class="container-fluid">
    <div class="row">
      <!-- Sidebar for Steps -->
      <div
        class="col-auto position-fixed start-0"
        style="top: 80px; z-index: 1020; padding-left: 0"
      >
        <div class="sidebar-card">
          <div class="list-group">
            <template
              v-for="(step, index) in steps.slice(0, 5)"
              :key="step.key"
            >
              <button
                class="list-group-item"
                :class="{ active: selectedStep === index }"
                @click="selectStep(index)"
                style="cursor: pointer; height: 70px; width: 70px"
                :data-bs-toggle="'tooltip'"
                :data-bs-placement="'right'"
                :title="$t('projectView.items.' + step.key)"
              >
                <i :class="step.icon"></i>
                <span>{{ $t("projectView.titleItems." + step.key) }}</span>
              </button>
              <div class="chevron-container" v-if="index < 4">
                <i class="bi bi-chevron-down"></i>
              </div>
            </template>
          </div>
          <div class="navigation-container">
            <StepNavigationButtons
              :isFirstStep="selectedStep === 0"
              :isLastStep="selectedStep === steps.length - 1"
            />
          </div>
          <button
            class="list-group-item save-button"
            @click="showSaveModal = true"
            style="cursor: pointer; height: 40px; width: 100px"
          >
            <span>{{ $t("projectView.saveButton") }}</span>
          </button>
        </div>
      </div>

      <!-- Main Content Area with offset for sidebar -->
      <div class="col-auto d-none d-sm-block" style="width: 120px"></div>
      <div class="col">
        <component :is="currentStepComponent" :project-id="projectId" />
      </div>

      <!-- Save Project Modal -->
      <div
        v-if="showSaveModal"
        class="modal-overlay"
        @click="showSaveModal = false"
      >
        <div class="modal-content" @click.stop>
          <h3>{{ $t("projectView.saveModal.title") }}</h3>
          <div class="d-flex gap-2">
            <button @click="showSaveModal = false" class="btn btn-secondary">
              {{ $t("projectView.saveModal.cancel") }}
            </button>
            <button @click="handleSaveProject" class="btn btn-primary">
              {{ $t("projectView.saveModal.save") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Validation Error Modal -->
      <div
        v-if="showErrorModal"
        class="modal-overlay"
        @click="showErrorModal = false"
      >
        <div class="modal-content error" @click.stop>
          <h3>{{ $t("projectView.validationModal.title") }}</h3>
          <p>{{ errorMessage }}</p>
          <div class="d-flex gap-2">
            <button @click="showErrorModal = false" class="btn">
              {{ $t("projectView.validationModal.ok") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Success Modal -->
      <div
        v-if="showSuccessModal"
        class="modal-overlay"
        @click="showSuccessModal = false"
      >
        <div class="modal-content success" @click.stop>
          <h3>{{ $t("projectView.successModal.title") }}</h3>
          <p>{{ $t("projectView.successModal.message") }}</p>
          <div class="d-flex gap-2">
            <button @click="showSuccessModal = false" class="btn">
              {{ $t("projectView.successModal.ok") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import * as bootstrap from "bootstrap";
import { useEquipmentStore } from "@/stores/equipment";
import { useProjectStore } from "@/stores/project";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";

// Import step components from components/projectComponents
import ProjectInfoComponent from "@/components/projectComponents/ProjectInfoComponent.vue";
import ProjectConsumptionComponent from "@/components/projectComponents/ProjectConsumptionComponent.vue";
import ProjectModulationComponent from "@/components/projectComponents/ProjectModulationComponent.vue";
import ProjectPanelsPositioningComponent from "@/components/projectComponents/ProjectPanelsPositioningComponent.vue";
import ProjectBatteryAndBackupComponent from "@/components/projectComponents/ProjectBatteryAndBackupComponent.vue";
import ProjectElectricComponent from "@/components/projectComponents/ProjectElectricComponent.vue";
import ProjectFinancialAnalysisComponent from "@/components/projectComponents/ProjectFinancialAnalysisComponent.vue";
import ProjectPreviewAndReportComponent2 from "@/components/projectComponents/ProjectPreviewAndReportComponent2.vue";
import StepNavigationButtons from "@/components/StepNavigationButtons.vue";

// Define the custom event type
type StepDirection = "next" | "back";

interface StepChangeDetail {
  direction: StepDirection;
}

export default defineComponent({
  name: "ProjectView",
  components: {
    ProjectInfoComponent,
    ProjectConsumptionComponent,
    ProjectModulationComponent,
    ProjectPanelsPositioningComponent,
    ProjectBatteryAndBackupComponent,
    ProjectElectricComponent,
    ProjectFinancialAnalysisComponent,
    ProjectPreviewAndReportComponent2,
    StepNavigationButtons,
  },
  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const projectId = route.params.id ?? null;
    const selectedStep = ref(0);
    const equipmentStore = useEquipmentStore();
    const projectStore = useProjectStore();
    const authStore = useAuthStore();
    const showSaveModal = ref(false);
    const showErrorModal = ref(false);
    const showSuccessModal = ref(false);
    const errorMessage = ref("");

    // Store tooltip elements for initialization and cleanup
    const tooltipElements: HTMLElement[] = [];
    const tooltipInstances: any[] = [];

    // Reference setting function for tooltips
    const setTooltipRef = (el: any) => {
      if (el && el instanceof HTMLElement) {
        tooltipElements.push(el);
      }
    };

    // Step definitions matching imported components
    const steps = [
      {
        key: "info",
        component: "ProjectInfoComponent",
        icon: "bi-layers",
      },
      {
        key: "consumption",
        component: "ProjectConsumptionComponent",
        icon: "bi-graph-up-arrow",
      },
      {
        key: "panelsPositioning",
        component: "ProjectPanelsPositioningComponent",
        icon: "bi-columns-gap",
      },
      {
        key: "batteryAndBackup",
        component: "ProjectBatteryAndBackupComponent",
        icon: "bi-battery-half",
      },
      {
        key: "previewAndReport",
        component: "ProjectPreviewAndReportComponent2",
        icon: "bi-file-earmark-spreadsheet",
      },
    ];

    const currentStepComponent = computed(
      () => steps[selectedStep.value].component
    );

    const selectStep = (index: number) => {
      selectedStep.value = index;
    };

    // Update tooltips when language changes
    const updateTooltips = () => {
      nextTick(() => {
        // For each tooltip element, update its title attribute with the new translation
        tooltipElements.forEach((el, index) => {
          // Determine which tooltip this is (step or save button)
          let newTitle = "";
          if (index < steps.length) {
            const stepKey = steps[index].key;
            newTitle = t(`projectView.items.${stepKey}`);
          } else {
            newTitle = t("projectView.saveButton");
          }

          // Update the data-bs-title attribute
          el.setAttribute("data-bs-title", newTitle);

          // If there's an existing tooltip instance, dispose it
          if (
            tooltipInstances[index] &&
            typeof tooltipInstances[index].dispose === "function"
          ) {
            tooltipInstances[index].dispose();
          }

          // Create a new tooltip with updated title
          try {
            tooltipInstances[index] = new bootstrap.Tooltip(el);
          } catch (error) {
            console.error("Error reinitializing tooltip:", error);
          }
        });
      });
    };

    // Initialize tooltips
    const initTooltips = () => {
      // Wait for DOM to be updated
      nextTick(() => {
        // Clean up any existing tooltips
        tooltipInstances.forEach((tooltip) => {
          if (tooltip && typeof tooltip.dispose === "function") {
            tooltip.dispose();
          }
        });
        tooltipInstances.length = 0;

        // Initialize tooltips on all elements
        tooltipElements.forEach((el) => {
          try {
            const tooltip = new bootstrap.Tooltip(el);
            tooltipInstances.push(tooltip);
          } catch (error) {
            console.error("Error initializing tooltip:", error);
          }
        });
      });
    };

    // Handle step navigation
    const handleStepChange = (event: Event) => {
      const customEvent = event as CustomEvent<StepChangeDetail>;
      const { direction } = customEvent.detail;

      if (direction === "next" && selectedStep.value < steps.length - 1) {
        selectedStep.value++;
      } else if (direction === "back" && selectedStep.value > 0) {
        selectedStep.value--;
      }
    };

    const handleSaveProject = async () => {
      try {
        if (projectStore.getCurrentProject) {
          // Validate project data before saving
          const validationResult = projectStore.dataValidatorBeforeSave(
            projectStore.getCurrentProject
          );

          if (!validationResult.isValid) {
            // Show error modal with validation message
            showSaveModal.value = false;
            showErrorModal.value = true;
            errorMessage.value =
              validationResult.message ||
              t("projectView.validationModal.defaultMessage");
            return;
          }

          // Set current user ID to the project before saving
          if (authStore.currentUser && authStore.currentUser.id) {
            projectStore.$patch({
              project: {
                ...projectStore.getCurrentProject,
                user_id: authStore.currentUser.id,
              },
            });
          }

          // Ensure total_price has only 2 decimal places
          if (projectStore.getCurrentProject.financial_data?.final_price) {
            projectStore.$patch({
              project: {
                ...projectStore.getCurrentProject,
                financial_data: {
                  ...projectStore.getCurrentProject.financial_data,
                  final_price:
                    Math.round(
                      projectStore.getCurrentProject.financial_data
                        .final_price * 100
                    ) / 100,
                },
              },
            });
          }

          // If validation passes, proceed with saving
          await projectStore.saveProject(projectStore.getCurrentProject);
          showSaveModal.value = false;
          showSuccessModal.value = true;
        }
      } catch (error) {
        console.error("Failed to save project:", error);
        showSaveModal.value = false;
        showErrorModal.value = true;
        errorMessage.value = t("projectView.errors.saveFailed");
      }
    };

    // Watch for language changes and update tooltips
    watch(
      () => locale.value,
      () => {
        // Update all tooltips when locale changes
        updateTooltips();
      }
    );

    onMounted(async () => {
      try {
        await equipmentStore.fetchEquipment();
      } catch (error) {
        console.error("Failed to load equipment data:", error);
        errorMessage.value = t("projectView.errors.equipmentLoadFailed");
        showErrorModal.value = true;
      }

      // Initialize tooltips
      initTooltips();

      // Add event listener for step navigation
      window.addEventListener("changeStep", handleStepChange);
    });

    onUnmounted(() => {
      // Remove event listener
      window.removeEventListener("changeStep", handleStepChange);

      // Dispose all tooltips
      tooltipInstances.forEach((tooltip) => {
        if (tooltip && typeof tooltip.dispose === "function") {
          tooltip.dispose();
        }
      });
    });

    return {
      projectId,
      selectedStep,
      steps,
      currentStepComponent,
      selectStep,
      showSaveModal,
      showErrorModal,
      showSuccessModal,
      errorMessage,
      handleSaveProject,
      setTooltipRef,
    };
  },
});
</script>

<style scoped>
.sidebar-card {
  width: 100px;
  height: 85vh;
  background-color: var(--color-white);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem 1rem 0 1rem;
  margin-left: 1rem;
  margin-right: 0.5rem;
  display: flex;
  justify-content: center;
  position: relative;
}

.list-group {
  border: none;
  background: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.list-group-item {
  border: none;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background: transparent;
  width: 70px;
  height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.list-group-item i {
  font-size: 1.5rem;
  margin: 0;
  padding: 0;
  line-height: 1;
  color: var(--color-dark);
}

.list-group-item span {
  font-size: 0.62rem;
  line-height: 1;
  margin: 0.4rem;
  padding: 0;
  color: inherit;
}

.list-group-item:hover {
  background-color: var(--color-light-light-gray);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.list-group-item.active {
  background-color: var(--color-white);
  border-color: transparent;
  color: var(--primary-color);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.list-group-item.active i {
  color: var(--primary-color);
}

/* Style for the save button in sidebar */
.save-button {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  border-radius: 0 0 2px 2px;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--color-white);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 70px;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
}

.save-button span {
  font-size: 0.75rem;
  line-height: 1.2;
  text-align: center;
  color: var(--color-white);
}

.save-button:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateX(-50%) translateY(-1px);
  filter: brightness(0.9);
}

@media (max-width: 576px) {
  .sidebar-card {
    width: 80px;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  .col-auto.d-none.d-sm-block {
    width: 100px !important;
  }

  .list-group {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    justify-content: center;
  }

  .list-group-item {
    border-right: 1px solid var(--color-light-gray);
    width: 60px !important;
    height: 60px !important;
  }
}

.container-fluid {
  padding-top: 60px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.3s ease-in-out;
}

.modal-content {
  background-color: var(--color-white);
  padding: 2rem;
  border-radius: 2px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: scaleIn 0.3s ease-in-out;
}

.modal-content h3 {
  margin-bottom: 1rem;
  color: var(--color-dark);
}

.modal-content p {
  margin-bottom: 1.5rem;
  color: var(--color-gray);
}

.modal-content .d-flex {
  justify-content: center;
}

.modal-content button {
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.modal-content button.btn-primary {
  background-color: var(--color-green);
  border-color: var(--color-green);
}

.modal-content button.btn-primary:hover {
  background-color: var(--color-green);
  border-color: var(--color-green);
  transform: scale(1.05);
  filter: brightness(0.9);
}

.modal-content button:hover {
  opacity: 0.9;
}

/* Success Modal Styles */
.modal-content.success {
  background-color: var(--color-green) !important;
  color: var(--color-white);
}

.modal-content.success h3 {
  color: var(--color-white);
}

.modal-content.success p {
  color: var(--color-white);
}

.modal-content.success button {
  background-color: var(--color-white);
  color: var(--color-green);
}

.modal-content.success button:hover {
  background-color: var(--color-light-light-gray);
}

/* Error Modal Styles */
.modal-content.error {
  background-color: var(--color-red) !important;
  color: var(--color-white);
}

.modal-content.error h3 {
  color: var(--color-white);
}

.modal-content.error p {
  color: var(--color-white);
}

.modal-content.error button {
  background-color: var(--color-white);
  color: var(--color-red);
}

.modal-content.error button:hover {
  background-color: var(--color-light-light-gray);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.chevron-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  margin: 5px 0;
}

.chevron-container i {
  font-size: 16px;
  color: var(--color-light-gray);
}

.navigation-container {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 0 1rem;
}
</style>
